import { FC } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import Footer from '@/components/Footer'
import Header from '@/components/Header'
import OrderPlacedMessage from '@/components/OrderPlacedMessage'
import Receipt, { ReceiptProps } from '@/components/Receipt'
import DGCBanner from '@/components/VenueSummary/DGCBanner'
import { Order } from '@/libs/helpers/apiClient'
import { HeaderVariant, OrderMethod } from '@/libs/helpers/utils'
import { FeatureFlagProps, SSRGlobalProps } from '@/libs/helpers/withServerSidePropsDecorator'
import { useConfiguration } from '@/providers/configuration/ConfigurationContext'
import { Button, DefaultThemeProvider, Grid, LoadScreen } from '@/tbui'

// 512px = Header + Footer
const StyledTemplate = styled(Grid)`
  min-height: calc(100vh - 512px);
  ${(props) =>
    props.theme.mixin.fluidRange(
      {
        prop: 'paddingBottom',
        fromSize: '30px',
        toSize: '124px',
      },
      {
        prop: 'paddingTop',
        fromSize: '30px',
        toSize: '124px',
      }
    )};
`

const StyledButton = styled(Button)`
  margin: auto;
`

const StyledBannerGrid = styled(Grid)`
  margin: 12px auto 20px auto;
  max-width: 375px;
`

type IOrderPlacedProps = {
  globals: SSRGlobalProps
  featureFlags: FeatureFlagProps
  order: Order | null
}

const OrderPlaced: FC<IOrderPlacedProps> = ({
  globals: {
    venue,
    loyaltyConfig,
    profile,
    config: { hasAdvancedDiscounts, isECardsEnabled },
  },
  order,
}) => {
  const { userSession } = useConfiguration()
  const { t } = useTranslation()

  const isDelivery = order?.orderMethod === OrderMethod.delivery
  const finalOrderLocation: ReceiptProps['finalOrderLocation'] = {
    lat: (isDelivery ? order?.deliveryAddress?.lat : venue?.latitude) ?? null,
    lng: (isDelivery ? order?.deliveryAddress?.lng : venue?.longitude) ?? null,
    displayAddress: isDelivery ? order?.deliveryAddress?.formattedAddress : venue?.address,
    phone: (isDelivery ? order?.phone : venue?.phone) ?? null,
  }

  // we're only showing the banner if gift cards are enabled && TBPayments is enabled for the CWA
  const showDGCBanner =
    (loyaltyConfig?.config != null &&
      loyaltyConfig?.config.giftCardsEnabled &&
      loyaltyConfig?.config.tbPaymentsRestaurantId != null) ||
    (venue?.giftCardBannerUrl != null && isECardsEnabled)

  if (order == null || !venue) {
    return <LoadScreen />
  }

  return (
    <>
      <DefaultThemeProvider>
        <Header
          variant={HeaderVariant.HOME}
          userProfile={profile ?? undefined}
          cart={{ isCartLoading: false, orderMethod: order.orderMethod }}
          venueXRefID={venue.venueXRefID}
        />
      </DefaultThemeProvider>
      <StyledTemplate data-test="order-placed" justify="center" container p="24px">
        <OrderPlacedMessage orderMethod={order.orderMethod} />
        <Receipt
          order={order}
          venue={venue}
          finalOrderLocation={finalOrderLocation}
          discountDetails={order.discountDetails}
          hasAdvancedDiscount={hasAdvancedDiscounts}
        />
        {showDGCBanner && (
          <StyledBannerGrid>
            <DGCBanner
              venueName={venue.name}
              isSignedIn={userSession.isSignedIn}
              loyaltyRestaurantGroupID={loyaltyConfig?.config?.tableUpGroupId ?? -1}
              isOrderPage
              isECardsEnabled={isECardsEnabled}
              giftCardBannerUrl={venue.giftCardBannerUrl ?? undefined}
            />
          </StyledBannerGrid>
        )}

        <StyledButton
          data-test="order-receipt-menu-button"
          onClick={() => {
            // eslint-disable-next-line no-restricted-properties
            window.location.href = `/${order.orderMethod.toLowerCase()}/${venue.venueXRefID}/menu`
          }}
        >
          {t('order.success.menu_button')}
        </StyledButton>
      </StyledTemplate>
      <DefaultThemeProvider>
        <Footer />
      </DefaultThemeProvider>
    </>
  )
}

export default OrderPlaced
